import React from 'react';
import { DialogProps } from '../../../../components/modals/BaseModal/types';
import {
  BaseModal,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '../../../../components/modals/BaseModal/BaseModal';
import { Autocomplete, Button, TableBody, TableHead, Tooltip } from '@mui/material';
import { IData } from '../../../../components/DragAndDrop/types';
import { TableRow } from '../../../../components/styled/TableRow';
import { TableCell } from '../../../../components/styled/TableCell';
import { MakeCodeTable } from '../../../../components/styled/MakeCodeTable';
import { useStyles } from '../AddMakeModelModal/styles';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/rootReducer';
import { autocompleteRender } from '../../../../utils/autocompleteRenders';
import { TextField } from '../../../../components/formControls/TextFieldStyled/TextField';
import { SystemIntegrationType, SystemType } from '../../../../store/reducers/serviceCenters/types';
import { useSCs } from '../../../../hooks/useSCs/useSCs';

type TModelCodesConfiguration = DialogProps & {
  configuredModels: IData[];
  onSaveModels: () => void;
  setConfiguredModels: React.Dispatch<React.SetStateAction<IData[]>>;
};
export const ModelCodesConfiguration: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<TModelCodesConfiguration>>
> = ({ onClose, configuredModels, setConfiguredModels, onSaveModels, ...props }) => {
  const { classes } = useStyles();
  const { selectedSC } = useSCs();
  const { makeModelCodes } = useSelector((state: RootState) => state.vehicleDetails);

  const handleUpdateCode = (prevEl: IData, value: string) => {
    setConfiguredModels(prev =>
      prev.map(el => (el.id === prevEl.id ? { ...el, code: value } : el))
    );
  };

  const getDesc = (code: string, isShort: boolean) => {
    const found = makeModelCodes.find(m => m.modelCode === code);
    if (!found) return '';
    const text = `${found.makeName} ${found.modelName}`;
    if (isShort) {
      return text.length > 29 ? text.slice(0, 29) + '...' : text;
    }

    return text;
  };

  const getIsActive = (code?: string | undefined) => {
    if (!code) return true;
    const found = makeModelCodes.find(m => m.modelCode === code);
    return found ? found.isActive : false;
  };

  return (
    <BaseModal {...props} width={860} height={770} onClose={onClose}>
      <DialogTitle onClose={onClose}>
        <span>
          {selectedSC?.system === SystemType.Xtime ? 'Xtime ' : 'CDK'} Model Codes Configuration
        </span>
      </DialogTitle>
      <p className={classes.codeTitle}>Configured Models</p>
      <DialogContent
        style={{
          marginBottom: '12px',
          backgroundColor: '#F7F8FB',
          padding: 0,
          margin: '10px 25px',
        }}
      >
        <MakeCodeTable stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width="60%" align="left" style={{ backgroundColor: 'rgb(247, 248, 251)' }}>
                Model
              </TableCell>
              <TableCell width="20%" align="left" style={{ backgroundColor: 'rgb(247, 248, 251)' }}>
                Model Code
              </TableCell>
              {selectedSC?.integration === SystemIntegrationType.Fortellis ? (
                <TableCell
                  width="30%"
                  align="left"
                  style={{ backgroundColor: 'rgb(247, 248, 251)' }}
                >
                  Model Description
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {configuredModels.map(el => (
              <TableRow key={el.id}>
                <TableCell align="left">{el.text}</TableCell>
                <TableCell>
                  <Autocomplete
                    options={makeModelCodes?.map(el => el.modelCode) ?? []}
                    style={{
                      width: '190px',
                      border: getIsActive(el.code) ? 'none' : '1px solid red',
                    }}
                    getOptionLabel={i => i}
                    value={el.code}
                    isOptionEqualToValue={(o, s) => o === s}
                    onChange={(e, newValue) => handleUpdateCode(el, newValue || '')}
                    renderInput={autocompleteRender({
                      label: '',
                      placeholder: 'Model Code',
                    })}
                  />
                </TableCell>
                {selectedSC?.integration === SystemIntegrationType.Fortellis ? (
                  <TableCell>
                    {getDesc(el.code || '', false).length > 29 ? (
                      <Tooltip placement="top" title={getDesc(el.code || '', false)}>
                        <strong style={{ cursor: 'pointer', userSelect: 'none' }}>
                          <TextField
                            id="description"
                            disabled
                            value={getDesc(el.code || '', true)}
                            onChange={() => {}}
                            error={false}
                            placeholder="Model Description"
                            name="Description"
                            inputProps={{
                              style: {
                                width: '250px',
                              },
                            }}
                          />
                        </strong>
                      </Tooltip>
                    ) : (
                      <TextField
                        id="description"
                        disabled
                        value={getDesc(el.code || '', true)}
                        onChange={() => {}}
                        error={false}
                        placeholder="Model Description"
                        name="Description"
                        inputProps={{
                          style: {
                            width: '250px',
                          },
                        }}
                      />
                    )}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </MakeCodeTable>{' '}
      </DialogContent>
      <DialogActions>
        <div className={classes.buttonsWrapper}>
          <Button onClick={onClose} className={classes.cancelButton}>
            Cancel
          </Button>
          <Button onClick={onSaveModels} className={classes.saveButton}>
            Save
          </Button>
        </div>
      </DialogActions>
    </BaseModal>
  );
};

export default ModelCodesConfiguration;
